import { AppBar, IconButton, Toolbar } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ShellAccountActions from "../../components/auth/ShellAccountActions";
import { useEffect } from "react";
import { useContentSetting } from "../../Hooks/ContentSetting";
import { useSnackbar } from "notistack";
import { useLocation } from "react-router-dom";
import ChatPopover from "../../components/Header/ChatPopover";
import AddReportHeader from "../../components/StudentReports/AddReportHeader";
import BroadcastMessage from "./HeaderPopups/BroadcastMessage";
import { handleShowNotification } from "../../utils/classRoomUtils";
import ExamNotification from "../../components/Header/ExamNotification";
import ThemeToggle from "../../components/GeneralComponents/ThemeToggle";
import HighlightHeaderButton from "../../components/ClassRoom/HighlightHeaderButton";
import LanguageSwitcher from "../../components/GeneralComponents/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { getTextDirection } from "../../i18n";
import { payloadMessage } from "../../utils/i18n/snackbarMessage";
import {
  isStudentReportsCapability,
  usesHifzClassScopedStudentLessons,
} from "../../utils/associateCapabilities";

export default function AppHeader({
  drawerWidth,
  handleDrawerToggle,
  handlePermanentDrawer,
  hideNavigation = false,
}) {
  const { socket, userInfo, setUserInfo, setUnreadMessageCount, unreadMessageCount } =
    useContentSetting();
  const { t, i18n } = useTranslation();
  const isShellRtl =
    getTextDirection(i18n.resolvedLanguage || i18n.language) === "rtl";
  /** MUI `theme.direction` is always `ltr` in ThemeConfig; i18n Urdu still puts the nav on the right, so `marginInlineStart` (theme-based) was always the physical left. Use physical sides so the app bar lines up with the real drawer edge. */
  const menuEdge = isShellRtl ? "end" : "start";
  const { user_type } = userInfo || {};
  const showAddReport =
    user_type !== "client" &&
    isStudentReportsCapability(userInfo) &&
    !usesHifzClassScopedStudentLessons(userInfo);
  const IS_NOTIFY = user_type === "teacher" || user_type === "associate";

  const { enqueueSnackbar } = useSnackbar();
  const { pathname } = useLocation();
  const is_class_route = pathname.includes("class-room");

  const handle_show_report_notification = (data) => {
    enqueueSnackbar(payloadMessage(data, t), { variant: "info", autoHideDuration: 25000 });
  };

  useEffect(() => {
    socket.on("added_report_by_student", (data) => {
      if (!IS_NOTIFY) return;
      handle_show_report_notification(data);
    });
    if (!is_class_route) {
      socket.on("send_message_from_class_receiver", (data) => {
        let find_user = data.members.find((user) => user._id === userInfo?._id);
        if (find_user && data.message.message_type !== "call") {
          handleShowNotification(data);
          setUnreadMessageCount((old) => old + 1);
        }
      });
    }

    return () => {
      socket.off("added_report_by_student");
      if (!is_class_route) {
        socket.off("send_message_from_class_receiver");
      }
    };
  }, [is_class_route, socket]);

  useEffect(() => {
    setTimeout(() => {
      const includes_path = (path) => {
        return pathname.toString().includes(path);
      };
      if (!includes_path("/students")) {
        localStorage.removeItem("students_filter");
      }
    }, 1000);
  }, [pathname]);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ...(isShellRtl
            ? { ml: { sm: 0 }, mr: { sm: `${drawerWidth}px` } }
            : { ml: { sm: `${drawerWidth}px` }, mr: { sm: 0 } }),
        }}
        className="app-header"
      >
        <Toolbar className="app-header-toolbar">
          {!hideNavigation ? (
            <>
              <IconButton
                color="inherit"
                aria-label={t("header.openNavigation")}
                edge={menuEdge}
                onClick={handleDrawerToggle}
                sx={{ me: 1, display: { xs: "inline-flex", sm: "none" } }}
              >
                <MenuIcon />
              </IconButton>
              <IconButton
                color="inherit"
                aria-label={t("header.openNavigation")}
                edge={menuEdge}
                onClick={handlePermanentDrawer}
                sx={{ me: 1, display: { xs: "none", sm: "inline-flex" } }}
              >
                <MenuIcon />
              </IconButton>
            </>
          ) : null}
        </Toolbar>
        <div className="d-flex gap-2 px-3 align-items-center app-header-actions">
          {user_type === "student" && <ExamNotification />}
          {unreadMessageCount > 0 && <ChatPopover />}
          {user_type === "associate" && <BroadcastMessage />}
          {showAddReport && <AddReportHeader />}
          <div className="app-header-tools">
            <LanguageSwitcher />
            <span className="app-header-tools__divider" aria-hidden />
            <HighlightHeaderButton />
            <ThemeToggle variant="header" />
            <ShellAccountActions
              clearLocalSession={() => {
                setUserInfo({});
                window.dispatchEvent(new Event("user-logout"));
              }}
            />
          </div>
        </div>
      </AppBar>
    </>
  );
}
